import type { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api-errors';
import {
  accountService,
  type FamilyMember,
  type FamilyMemberPayload,
} from '@/services/account.service';

const SILENT_LIST_STATUSES = new Set([403, 404]);
/**
 * Backend concluiu a ação, mas o e-mail de convite não foi entregue. O código
 * vem no `detail`: um 502 sem ele é falha de gateway (proxy fora do ar, backend
 * reiniciando) e nada foi criado.
 */
const EMAIL_NOT_SENT_CODE = 'INVITE_EMAIL_NOT_SENT';

const apiDetail = (error: unknown): unknown =>
  (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;

const notifyFailure = (error: unknown, fallback: string) => {
  toast({ title: getApiErrorMessage(apiDetail(error), fallback), variant: 'destructive' });
};

/** Usuários vinculados à conta do titular: listagem, convite, edição, reenvio e remoção. */
export function useAccountMembers(enabled: boolean) {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [maxUsers, setMaxUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const reset = () => {
    setMembers([]);
    setCanManage(false);
    setMaxUsers(0);
  };

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await accountService.getFamilyMembers();
      setMembers(response.users || []);
      setCanManage(response.can_manage_users);
      setMaxUsers(response.max_users || 0);
    } catch (error: unknown) {
      const statusCode = (error as AxiosError)?.response?.status;
      if (!statusCode || !SILENT_LIST_STATUSES.has(statusCode)) {
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os usuários da conta.',
          variant: 'destructive',
        });
      }
      reset();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      void refresh();
    } else {
      reset();
    }
  }, [enabled, refresh]);

  const runMutation = async (
    mutation: () => Promise<unknown>,
    successTitle: string,
    failureMessage: string,
    successDescription?: string,
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      await mutation();
      toast({ title: successTitle, description: successDescription });
      await refresh();
      return true;
    } catch (error: unknown) {
      const detail = apiDetail(error);
      const partialSuccess =
        typeof detail === 'string' && detail.startsWith(`${EMAIL_NOT_SENT_CODE}|`);
      if (partialSuccess) {
        toast({
          title: 'E-mail não enviado',
          description: detail.slice(EMAIL_NOT_SENT_CODE.length + 1),
          variant: 'destructive',
        });
      } else {
        notifyFailure(error, failureMessage);
      }
      // Mesmo na falha o estado pode ter mudado no backend: recarrega a lista.
      await refresh();
      return partialSuccess;
    } finally {
      setIsSaving(false);
    }
  };

  const inviteMember = (payload: FamilyMemberPayload) =>
    runMutation(
      () => accountService.createFamilyMember({ name: payload.name.trim(), email: payload.email.trim() }),
      'Convite enviado',
      'Não foi possível enviar o convite.',
      'O usuário receberá um e-mail para criar a senha de acesso.',
    );

  const updateMember = (memberId: string, payload: FamilyMemberPayload) =>
    runMutation(
      () =>
        accountService.updateFamilyMember(memberId, {
          name: payload.name.trim(),
          email: payload.email.trim(),
        }),
      'Usuário atualizado com sucesso',
      'Não foi possível salvar o usuário.',
    );

  const resendInvite = (member: FamilyMember) =>
    runMutation(
      () => accountService.resendFamilyMemberInvite(member.id),
      'Convite reenviado',
      'Não foi possível reenviar o convite.',
      `Um novo link foi enviado para ${member.email}.`,
    );

  const removeMember = (member: FamilyMember) =>
    runMutation(
      () => accountService.deleteFamilyMember(member.id),
      'Usuário removido com sucesso',
      'Não foi possível remover o usuário.',
    );

  return {
    members,
    canManage,
    maxUsers,
    availableSlots: Math.max(maxUsers - members.length, 0),
    isLoading,
    isSaving,
    inviteMember,
    updateMember,
    resendInvite,
    removeMember,
  };
}
